import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, Lock, Scale } from "lucide-react";

/**
 * Combined legal page — renders Terms, Privacy, and Safety in one scrollable
 * view. Users can deep-link to a specific section via /terms#privacy or
 * /terms#safety; the header nav jumps to each.
 */
export default function LegalPage() {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  }, [location.hash]);

  return (
    <div className="py-8 max-w-4xl mx-auto space-y-10" data-testid="legal-page">
      <header className="space-y-3 pb-6 border-b border-[#F5F2E8]/10">
        <div className="overline" style={{ color: "#D7A62A" }}>The fine print</div>
        <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight leading-[0.95]">
          Terms, Privacy & <span style={{ color: "#E53935" }}>Safety</span>
        </h1>
        <p className="text-[#F5F2E8]/70 text-sm md:text-base leading-relaxed">
          Read these carefully before you hunt. By using The Local Loot you agree to everything below.
        </p>
        <nav className="flex flex-wrap gap-2 pt-2">
          <a href="#terms" className="btn-ghost text-xs inline-flex items-center gap-1"><Scale size={12} /> Terms of Use</a>
          <a href="#privacy" className="btn-ghost text-xs inline-flex items-center gap-1"><Lock size={12} /> Privacy Policy</a>
          <a href="#safety" className="btn-ghost text-xs inline-flex items-center gap-1"><ShieldAlert size={12} /> Safety & Liability</a>
        </nav>
      </header>

      {/* ===== Terms ===== */}
      <section id="terms" className="space-y-4 scroll-mt-24">
        <div className="flex items-center gap-2">
          <Scale size={18} style={{ color: "#76B82A" }} />
          <h2 className="font-display uppercase text-3xl md:text-4xl">1. Terms of Use</h2>
        </div>
        <div className="font-mono text-xs text-[#F5F2E8]/50 uppercase tracking-widest">
          Effective Date: April 22, 2026
        </div>
        <p className="text-[#F5F2E8]/85 leading-relaxed">
          By using The Local Loot website or participating in any "Loot Drop," you agree to the following terms:
        </p>
        <div className="space-y-3 text-[#F5F2E8]/85 leading-relaxed">
          <div>
            <h3 className="font-display uppercase text-lg text-[#D7A62A]">Eligibility</h3>
            <p>You must be 18 years of age or older to participate. If under 18, you must be accompanied by a parent or legal guardian.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg text-[#D7A62A]">The "Rules of the Hunt"</h3>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Loot is always placed in public, accessible areas.</li>
              <li><strong>No Trespassing:</strong> You agree never to enter private property, restricted areas, or dangerous locations (railroads, construction sites, etc.) while hunting.</li>
              <li><strong>First Come, First Served:</strong> The first person to physically find the loot and claim it is the sole winner.</li>
            </ul>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg text-[#D7A62A]">Code of Conduct</h3>
            <p>You agree to be respectful to other hunters and the public. Any reports of harassment, dangerous driving, or "sabotaging" other players will result in a permanent ban from the platform.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg text-[#D7A62A]">Prize Claims</h3>
            <p>Once loot is found, it is yours. The Local Loot does not facilitate digital transfers; the prize is the physical "loot" found at the coordinates.</p>
          </div>
        </div>
      </section>

      {/* ===== Privacy ===== */}
      <section id="privacy" className="space-y-4 scroll-mt-24 pt-4 border-t border-[#F5F2E8]/10">
        <div className="flex items-center gap-2">
          <Lock size={18} style={{ color: "#1e88e5" }} />
          <h2 className="font-display uppercase text-3xl md:text-4xl">2. Privacy Policy</h2>
        </div>
        <div className="font-mono text-xs text-[#F5F2E8]/50 uppercase tracking-widest">
          Last Updated: April 2026
        </div>
        <p className="text-[#F5F2E8]/85 leading-relaxed">
          At The Local Loot, we value your privacy. Because our game is based on your physical location, here is exactly how we handle your data:
        </p>
        <div className="space-y-3 text-[#F5F2E8]/85 leading-relaxed">
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#1e88e5" }}>Location Services</h3>
            <p>To use the "Hot or Cold" feature, you must allow your browser or device to access your location. We do not store your movement history on our servers. Your location is used solely in real-time to calculate your distance from the loot coordinates.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#1e88e5" }}>No Third-Party Tracking</h3>
            <p>We do not sell, rent, or share your location data or personal information with third-party advertisers or data brokers.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#1e88e5" }}>What We Collect</h3>
            <p>We only collect information you voluntarily provide (such as your email if you sign up for alerts or your social media handle if you tag us in a win).</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#1e88e5" }}>Security</h3>
            <p>While we don't store your "path" to the loot, we use standard encryption to protect the website and any communications between your device and our map.</p>
          </div>
        </div>
      </section>

      {/* ===== Safety ===== */}
      <section
        id="safety"
        className="space-y-4 scroll-mt-24 pt-4 border-t border-[#F5F2E8]/10"
        style={{ color: "#F5F2E8" }}
      >
        <div className="flex items-center gap-2">
          <ShieldAlert size={18} style={{ color: "#E53935" }} />
          <h2 className="font-display uppercase text-3xl md:text-4xl">3. Safety Disclaimer & Liability Waiver</h2>
        </div>
        <div
          className="p-3 border font-mono text-xs uppercase tracking-widest"
          style={{ color: "#E53935", borderColor: "#E53935", background: "rgba(229,57,53,0.06)" }}
        >
          Read carefully — this limits your legal rights.
        </div>
        <p className="text-[#F5F2E8]/85 leading-relaxed">
          By participating in The Local Loot, you acknowledge and agree to the following:
        </p>
        <div className="space-y-3 text-[#F5F2E8]/85 leading-relaxed">
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#E53935" }}>Assumption of Risk</h3>
            <p>Outdoor "hunting" involves inherent risks, including but not limited to: uneven terrain, weather conditions, traffic, and physical exertion. You participate entirely at your own risk.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#E53935" }}>Release of Liability</h3>
            <p>To the maximum extent permitted by Tennessee law, The Local Loot (and its owners, sponsors, and partners) shall not be liable for any personal injury, property damage, or legal issues (such as trespassing fines) incurred during your participation.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#E53935" }}>"As-Is" Technology</h3>
            <p>We provide GPS coordinates and "Hot or Cold" signals as a game mechanic. We are not responsible for GPS inaccuracies, "dead zones," or technology glitches that may affect your hunt.</p>
          </div>
          <div>
            <h3 className="font-display uppercase text-lg" style={{ color: "#E53935" }}>Public Safety</h3>
            <p>You agree to remain aware of your surroundings at all times. Do not use your phone while driving, biking, or walking in traffic.</p>
          </div>
        </div>
      </section>

      <footer className="pt-8 border-t border-[#F5F2E8]/10 font-mono text-[10px] uppercase tracking-widest text-[#F5F2E8]/40 flex items-center justify-between flex-wrap gap-2">
        <span>Questions? Email <a href="mailto:hi@thelocalloot.com" className="text-[#D7A62A] underline">hi@thelocalloot.com</a></span>
        <Link to="/home" className="hover:text-[#76B82A]">← Back to home</Link>
      </footer>
    </div>
  );
}
